import ChannelsAvailabilityDialog from "@dashboard/components/ChannelsAvailabilityDialog";
import { ChannelFragment } from "@dashboard/graphql";
import useStateFromProps from "@dashboard/hooks/useStateFromProps";
import { DialogProps } from "@dashboard/types";
import { arrayDiff } from "@dashboard/utils/arrays";
import { toggle } from "@dashboard/utils/lists";
import { Box, Text } from "@saleor/macaw-ui-next";
import { defineMessages, useIntl } from "react-intl";

import { ProductUpdateData } from "./types";

const messages = defineMessages({
  title: {
    id: "Eau5AV",
    defaultMessage: "Manage Products Channel Availability",
  },
  variantPriceWarning: {
    id: 'BphmSA',
    defaultMessage:
      "Remember to set prices for product variants in the newly added channels. Products without prices will not be available for purchase.",
  },
});

export type ProductChannelsListingDialogSubmit = (
  update: Record<"added" | "removed", string[]>,
) => void;

interface ProductChannelsListingsDialogProps extends DialogProps {
  channels: ChannelFragment[];
  data: Pick<ProductUpdateData, "channels">;
  onConfirm: ProductChannelsListingDialogSubmit;
  hasVariants?: boolean;
}

const ProductChannelsListingsDialog = ({
  channels,
  data,
  open,
  onClose,
  onConfirm,
  hasVariants = false,
}: ProductChannelsListingsDialogProps) => {
  const intl = useIntl();
  const [selected, setSelected] = useStateFromProps(
    data.channels.updateChannels?.map(listing => listing.channelId) ?? [],
  );

  const currentChannelIds = data.channels.updateChannels?.map(({ channelId }) => channelId) ?? [];
  const { added: newlyAddedChannels } = arrayDiff(currentChannelIds, selected);
  const hasNewChannels = newlyAddedChannels.length > 0;

  const handleConfirm = () => {
    onConfirm(arrayDiff(currentChannelIds, selected));
    onClose();
  };
  const handleToggleAll = () =>
    selected.length !== channels.length
      ? setSelected(channels.map(({ id }) => id))
      : setSelected([]);

  return (
    <ChannelsAvailabilityDialog
      toggleAll={handleToggleAll}
      isSelected={({ id }) => selected.includes(id)}
      channels={channels}
      onChange={({ id }) => setSelected(toggle(id, selected, (a, b) => a === b))}
      onClose={onClose}
      open={open}
      title={intl.formatMessage(messages.title)}
      confirmButtonState="default"
      selected={selected?.length}
      onConfirm={handleConfirm}
    >
      {hasVariants && hasNewChannels && (
        <Box
          backgroundColor="warning1"
          borderColor="warning1"
          borderWidth={1}
          borderStyle="solid"
          borderRadius={2}
          padding={3}
          marginTop={3}
        >
          <Text size={2} color="default1">
            {intl.formatMessage(messages.variantPriceWarning)}
          </Text>
        </Box>
      )}
    </ChannelsAvailabilityDialog>
  );
};

ProductChannelsListingsDialog.displayName = "ProductChannelsListingsDialog";
export default ProductChannelsListingsDialog;
